import { addWeeks, startOfDay } from "date-fns"
import * as z from "zod"
import { debouncedValidatePhoneNumber } from "@/lib/validate-phone-number"
import { validatePhoneNumber } from "@/modules/inbound-wizard/schema/phone-number-schema"
import { isDateRangeValid } from "@/modules/patients/agenda/time-window/lib/calculate-date-range"
import { phoneNumberValidation } from "@/modules/patients/components/phoneNumber/PhoneNumberInput"
import { ReferralReason, ReferralReasonValues, ReferredByValues } from "@/types/__generated__/union-types"

const emergencyReasons: ReferralReason[] = ["EMERGENCY_POST_REGISTRATION", "EMERGENCY_REGISTRATION"]

export const checkReasonIfPost = (reason: ReferralReason) => {
  return reason === "APPOINTMENT_POST_REGISTRATION" || reason === "EMERGENCY_POST_REGISTRATION"
}

export const isEmergencyReason = (reason: ReferralReason): boolean => emergencyReasons.includes(reason)

export const correctionValues = ["timeWindow", "timeWindowErrorCorrection"] as const
export type ErrorCorrection = (typeof correctionValues)[number]

export const timeWindowTextMaxLength = 255

export const timeWindowFormSchema = z
  .object({
    id: z.string().optional(),
    nameOfDoctor: z.string().max(100, { message: "common.fieldErrorList.characterLimit" }).optional(),
    internalComment: z
      .string()
      .max(timeWindowTextMaxLength, { message: "common.fieldErrorList.characterLimit" })
      .optional(),
    dateFrom: z.date({ error: "common.fieldErrorList.invalidDateRange" }),
    dateTo: z.date({ error: "common.fieldErrorList.invalidDateRange" }),
    referredBy: z.enum(ReferredByValues, { error: "common.fieldErrorList.requiredField" }),
    updatedAt: z.date().readonly().optional(),
    updatedBy: z.string().optional(),
    createdAt: z.date().readonly().optional(),
    createdBy: z.string().optional(),
    involvesPreventionProgram: z.boolean(),
    referredByExternalCareProviderId: z.string().optional(),
    alternativeTriage: z.boolean().optional(),
    alternativeTriagePhoneNumber: phoneNumberValidation(debouncedValidatePhoneNumber()).optional(),
    alternativeTriageReason: z
      .string()
      .max(timeWindowTextMaxLength, { message: "common.fieldErrorList.characterLimit" })
      .optional(),
    referralToCareProviderId: z.string().optional(),
    referralReason: z.enum(ReferralReasonValues),
    correction: z.enum(correctionValues),
    referenceDate: z.date().optional(),
  })
  .superRefine((data, ctx) => {
    if (!isDateRangeValid(data)) {
      ctx.addIssue({
        code: "custom",
        message: "common.fieldErrorList.invalidDateRange",
        path: ["dateFrom"],
      })
      ctx.addIssue({ code: "custom", message: "common.fieldErrorList.invalidDateRange", path: ["dateTo"] })
    }

    if (data.correction === "timeWindowErrorCorrection" && data.referenceDate === undefined) {
      ctx.addIssue({
        code: "custom",
        message: "common.fieldErrorList.requiredField",
        path: ["referenceDate"],
      })
    }

    if (data.referredBy === "CARE_PROVIDER" && !data.referredByExternalCareProviderId) {
      ctx.addIssue({
        code: "custom",
        message: "common.fieldErrorList.requiredField",
        path: ["referredByExternalCareProviderId"],
      })
    }

    if (!isEmergencyReason(data.referralReason) && !data.referralToCareProviderId) {
      ctx.addIssue({
        code: "custom",
        message: "common.fieldErrorList.requiredField",
        path: ["referralToCareProviderId"],
      })
    }

    if (data.alternativeTriage && data.referredBy === "MEDGATE") {
      validatePhoneNumber(ctx, data.alternativeTriagePhoneNumber, ["alternativeTriagePhoneNumber"])
    }
  })

export type TimeWindowFormSchema = z.infer<typeof timeWindowFormSchema>

export interface AlternativeTriageData {
  phoneNumber: string
  reason: string
}

export type TimeWindowNavigationState = {
  alternativeTriage?: AlternativeTriageData
}

export const initialTimeWindowState: Omit<TimeWindowFormSchema, "referredBy"> = {
  id: undefined,
  dateFrom: new Date(),
  referralToCareProviderId: "",
  referredByExternalCareProviderId: "",
  referralReason: "APPOINTMENT_REGISTRATION",
  dateTo: addWeeks(new Date(), 8),
  internalComment: "",
  nameOfDoctor: "",
  correction: "timeWindow",
  involvesPreventionProgram: false,
  alternativeTriage: false,
  referenceDate: startOfDay(new Date()),
}
